import { useEffect, useState } from "react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  AreaChart, Area, XAxis, YAxis,
} from "recharts";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fetchBasket } from "@/lib/api";
import { SimTag, timeAgo } from "@/components/shared";
import ActivityFeed from "@/components/ActivityFeed";

const ACTIVITY_COLORS = {
  BUY: "bg-[#14F195]/20 text-[#14F195]",
  SWAP: "bg-[#9945FF]/20 text-[#9945FF]",
  DISTRIB: "bg-[#00F0FF]/20 text-[#00F0FF]",
};

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-panel rounded-lg px-3 py-2 text-xs mono-font">
      <span className="text-[#14F195]">{payload[0].name}: </span>
      <span className="text-white">{payload[0].value}</span>
    </div>
  );
};

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchBasket().then(setData).catch(() => {});
  }, []);

  const assets = data?.assets ?? [];
  const perf = data?.performance ?? [];

  return (
    <div data-testid="dashboard-page" className="bg-[#07050d]">
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <div className="flex items-center gap-3">
                <span className="mono-font text-xs uppercase tracking-widest text-[#14F195] font-semibold">Live Simulation</span>
                <SimTag>Simulated Prototype Data</SimTag>
              </div>
              <h1 className="display-font uppercase text-3xl md:text-5xl text-white mt-2">Basket Treasury Dashboard</h1>
            </div>
            <div className="mt-4 md:mt-0 text-xs mono-font text-[#9b93b8]" data-testid="last-update-indicator">
              Last Simulated Batch: <span className="text-[#14F195]">{data ? timeAgo(data.last_update) : "—"}</span>
            </div>
          </div>

          {/* KPI row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {[
              { label: "Treasury Balance", value: `$${(data?.treasury_value_usd ?? 248950).toLocaleString()}`, accent: "text-white", testid: "kpi-treasury" },
              { label: "Basket Assets", value: assets.length || 5, accent: "text-[#14F195]", testid: "kpi-assets" },
              { label: "Simulated Batches", value: 48, accent: "text-[#00F0FF]", testid: "kpi-batches" },
              { label: "Fee Rate", value: "4.0%", accent: "text-[#9945FF]", testid: "kpi-fee" },
            ].map((k) => (
              <div key={k.label} data-testid={k.testid} className="glass-panel rounded-2xl p-5">
                <div className="text-[11px] text-[#9b93b8] uppercase tracking-wider">{k.label}</div>
                <div className={`text-2xl font-bold mono-font mt-1 ${k.accent}`}>{k.value}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Allocation donut */}
            <div className="lg:col-span-5 glass-panel rounded-2xl p-6" data-testid="allocation-chart">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-white mono-font">Basket Allocation</h3>
                <SimTag>Demo</SimTag>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={assets} dataKey="allocation" nameKey="symbol" innerRadius="58%" outerRadius="85%" stroke="#07050d" strokeWidth={3} paddingAngle={2} isAnimationActive={true} animationDuration={1200}>
                      {assets.map((a) => (
                        <Cell key={a.symbol} fill={a.color} style={{ filter: `drop-shadow(0 0 6px ${a.color}66)` }} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap justify-center gap-3 mt-2">
                {assets.map((a) => (
                  <span key={a.symbol} className="flex items-center gap-1.5 text-[11px] mono-font text-[#b5aed0]">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: a.color }} />
                    {a.symbol} {a.allocation}%
                  </span>
                ))}
              </div>
            </div>

            {/* Performance chart */}
            <div className="lg:col-span-7 glass-panel rounded-2xl p-6" data-testid="performance-chart">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-white mono-font">Accumulation Curve</h3>
                <span className="text-xs text-[#14F195] mono-font">30D Prototype View</span>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={perf} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="perfGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#14F195" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#9945FF" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="day" tick={{ fill: "#9b93b8", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.08)" }} interval={6} />
                    <YAxis hide domain={["dataMin - 2", "dataMax + 2"]} />
                    <Tooltip content={<ChartTooltip />} />
                    <Area type="monotone" dataKey="value" name="Index" stroke="#14F195" strokeWidth={2.5} fill="url(#perfGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <p className="text-[11px] text-[#7a7493] mono-font mt-3">Simulated basket index (base 100). Not investment performance.</p>
            </div>

            {/* Asset list */}
            <div className="lg:col-span-7 glass-panel rounded-2xl p-6" data-testid="asset-list">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-white mb-4 mono-font">Curated Asset Weightings</h3>
              <div className="space-y-3">
                {assets.map((a) => (
                  <div key={a.symbol} data-testid={`asset-row-${a.symbol.toLowerCase()}`}
                    className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/5 hover:border-[#9945FF]/40 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs mono-font"
                        style={{ background: `${a.color}22`, border: `1px solid ${a.color}66`, color: a.color }}>
                        {a.symbol}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-white">{a.name}</div>
                        <div className="text-[11px] text-[#9b93b8] mono-font">${a.price_demo} (Demo)</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-white mono-font">{a.allocation}% Allocation</div>
                      <div className="text-[11px] text-[#14F195] mono-font">${a.volume_demo.toLocaleString()} Vol</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Activity: purchases / distributions / all */}
            <div className="lg:col-span-5 glass-panel rounded-2xl p-6" data-testid="purchase-activity">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-white mono-font">Ecosystem Activity</h3>
                <span className="w-2 h-2 rounded-full bg-[#14F195] animate-pulse" />
              </div>
              <Tabs defaultValue="all">
                <TabsList className="bg-black/40 border border-white/10 mb-4" data-testid="activity-tabs">
                  <TabsTrigger value="all" data-testid="activity-tab-all">All</TabsTrigger>
                  <TabsTrigger value="purchases" data-testid="activity-tab-purchases">Purchases</TabsTrigger>
                  <TabsTrigger value="distributions" data-testid="activity-tab-distributions">Distributions</TabsTrigger>
                </TabsList>

                <TabsContent value="all">
                  <div className="space-y-3 max-h-[380px] overflow-y-auto pr-1">
                    {(data?.recent_activity ?? []).map((act, i) => (
                      <div key={i} className="p-3 rounded-xl bg-black/40 border border-white/5 hover:border-[#9945FF]/30 transition-colors">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold mono-font ${ACTIVITY_COLORS[act.type]}`}>{act.type}</span>
                          <span className="text-[10px] text-[#9b93b8] mono-font ml-auto">{act.minutes_ago}m ago</span>
                        </div>
                        <p className="text-xs text-[#d3cbef] mono-font leading-relaxed">{act.text}</p>
                        <p className="text-[10px] text-[#7a7493] mono-font mt-1">Tx: {act.tx} (Demo)</p>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="purchases">
                  <div className="space-y-3 max-h-[380px] overflow-y-auto pr-1" data-testid="purchases-list">
                    {(data?.recent_activity ?? []).filter((a) => a.type !== "DISTRIB").map((act, i) => (
                      <div key={i} className="p-3 rounded-xl bg-black/40 border border-white/5 hover:border-[#14F195]/30 transition-colors">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold mono-font ${ACTIVITY_COLORS[act.type]}`}>{act.type}</span>
                          <span className="text-[10px] text-[#9b93b8] mono-font ml-auto">{act.minutes_ago}m ago</span>
                        </div>
                        <p className="text-xs text-[#d3cbef] mono-font leading-relaxed">{act.text}</p>
                        <p className="text-[10px] text-[#7a7493] mono-font mt-1">Tx: {act.tx} (Demo)</p>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="distributions">
                  <div className="space-y-3 max-h-[380px] overflow-y-auto pr-1" data-testid="distributions-list">
                    {(data?.distributions ?? []).map((d) => (
                      <div key={d.batch} className="p-3 rounded-xl bg-black/40 border border-white/5 hover:border-[#00F0FF]/30 transition-colors">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold mono-font ${ACTIVITY_COLORS.DISTRIB}`}>DISTRIB</span>
                          <span className="text-[10px] text-[#9b93b8] mono-font ml-auto">{d.minutes_ago}m ago</span>
                        </div>
                        <p className="text-xs text-[#d3cbef] mono-font leading-relaxed">
                          Batch #{d.batch} — ${d.value_usd.toLocaleString()} allocated to {d.holders.toLocaleString()} eligible holders
                        </p>
                        <p className="text-[10px] text-[#7a7493] mono-font mt-1">Snapshot verified (Demo)</p>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* Full ecosystem activity timeline */}
          <div className="mt-8">
            <ActivityFeed items={data?.activity ?? []} />
          </div>
        </div>
      </section>
    </div>
  );
}
